import { useState, type ReactElement, useContext, useEffect } from 'react'
import {
  Box,
  Grid,
  styled,
  Typography,
  Stack,
  Tooltip,
  Button,
  List,
  ListItem,
  ListItemButton,
  Checkbox,
  Divider,
} from '@mui/material'
import CardContainer from '@/components/dashboard/CardContainer'
import TokenIcon from '@/components/common/TokenIcon'
import { formatCurrency } from '@/utils/formatNumber'
import { useAppSelector } from '@/store'
import { selectCurrency } from '@/store/settingsSlice'
import useDesktop from '@/hooks/useDesktop'
import { Body2White } from '@/components/typography'
import MuiLink from '@mui/material/Link'
import ModalButton from '@/components/common/ModalButton'
// import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import EthHashInfo from '@/components/common/EthHashInfo'
import { formatDateTime } from '@/utils/date'
import { formatVisualAmount, safeParseUnits, shortenText } from '@/utils/formatters'

// import DoneAllIcon from '@mui/icons-material/DoneAll'
import { RuleType } from '@/store/setUpInheritorsV2Slice'
import { type InheritanceOfInheritor } from '@/utils/api'
import { SendProcessingContext } from '@/components/inheritors/SendProcessing'
import { ModalButtonContext } from '@/components/common/ModalButton'
import { NoticeInheritanceContext } from '@/components/dashboard/NoticeInheritance/NoticeInheritance'
// import useInheritanceOfInheritor from '@/hooks/useInheritanceOfInheritor'

// import { PendingStatus } from '@/store/pendingTxsSlice'

const Btn = styled(Button)(`
    height: 32px;
    padding: 10px 16px;
    font-size: 14px;
    font-style: normal;
    font-weight: bold;
    line-height: 160%;
    border-radius: 4px;
    border: 1px solid var(--Emerald-600, #22C55E);
`)

const StyledList = styled(List)({
  padding: 0,
  '& .MuiListItemButton-root:hover': {
    backgroundColor: 'var(--Supporting-Dark-BG-700, #272A3E)',
  },
})

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

interface TokenItem {
  logoURI: string
  name: string
  symbol: string
  amount: string
  amountInUSD: string
  address: string
}

const formatName = (name: string, maxLength: number): string => {
  return name.length > maxLength ? `${name.slice(0, maxLength)}...` : name
}

const ClaimableAssets = ({
  inheritance,
  isModal = false,
}: {
  inheritance: InheritanceOfInheritor
  isModal?: boolean
}): ReactElement => {
  const { ruleInfo, balances, percent } = inheritance
  const currency = useAppSelector(selectCurrency)
  const isNoToken = balances.items.length === 1 && balances.items[0].balance === '0'

  const tokenList: TokenItem[] = balances.items.map((item) => {
    const amount = safeParseUnits(item.balance, 0)?.mul(percent).div(10_000).toString() || '0'
    const amountInUSD = formatCurrency((parseFloat(item.fiatBalance) * percent) / 10_000, currency)

    return {
      logoURI: item.tokenInfo.logoUri,
      name: item.tokenInfo.name,
      symbol: item.tokenInfo.symbol,
      amount: formatVisualAmount(amount, item.tokenInfo.decimals, 6),
      // amount,
      amountInUSD,
      address: item.tokenInfo.address,
    }
  })

  const ruleRender = () => {
    if (ruleInfo.type === RuleType.AfterInactivityPeriod) {
      return (
        <>
          <Body2White sx={{ color: 'var(--Ebony-Clay-300, #D2D7E5)', lineHeight: '140%' }}>
            {ruleInfo.period.value} {ruleInfo.period.type}
            {ruleInfo.period.value > 1 ? 's' : ''} of inactivity
          </Body2White>
        </>
      )
    } else if (ruleInfo.timestamp) {
      return (
        <>
          <Body2White sx={{ color: 'var(--Ebony-Clay-300, #D2D7E5)', lineHeight: '140%' }}>
            Distributed on {formatDateTime(ruleInfo.timestamp * 1000)}
          </Body2White>
        </>
      )
    }
  }

  return (
    <CardContainer title={''}>
      <Box flex={1} sx={{ width: '100%' }}>
        <Grid
          container
          sx={{
            marginTop: '16px',
            paddingX: '16px',
            paddingY: '8px',
            borderRadius: '6px',
            border: '1px solid var(--Ebony-Clay-950, #2F354B)',
            background: 'var(--Supporting-Dark-BG-700, #272A3E)',
          }}
        >
          <Grid item container xs={12}>
            <Grid item container xs="auto" justifyContent="center" alignContent="center">
              <Tooltip title="Who left you this crypto legacy" placement="bottom">
                <Body2White sx={{ color: 'var(--Ebony-Clay-500)', width: '100px' }}>Legator:</Body2White>
              </Tooltip>
            </Grid>
            <Grid item container xs alignContent="center">
              <EthHashInfo
                address={inheritance.legator}
                showName={false}
                shortAddress={false}
                showAvatar={false}
                shortenLen={6}
                showPrefix={false}
                copyAddress={true}
                hasExplorer={true}
                color={'var(--Ebony-Clay-300, #D2D7E5)'}
                fontWeight={'500'}
              />
            </Grid>
          </Grid>

          <Grid item container xs={12} mt="4px">
            <Grid item container xs="auto">
              <Body2White sx={{ color: 'var(--Ebony-Clay-500)', width: '100px' }}>Condition:</Body2White>
            </Grid>
            <Grid item container xs alignContent="center">
              <Body2White sx={{ color: 'var(--Ebony-Clay-300, #D2D7E5)', lineHeight: '140%' }}>
                {ruleRender()}
              </Body2White>
            </Grid>
          </Grid>

          {ruleInfo.type === RuleType.AfterInactivityPeriod && (
            <Grid item container xs={12} mt="4px">
              <Grid item container xs="auto">
                <Body2White sx={{ color: 'var(--Ebony-Clay-500)', width: '100px' }}>Last activity:</Body2White>
              </Grid>
              <Grid item container xs alignContent="center">
                <Body2White sx={{ color: 'var(--Ebony-Clay-300, #D2D7E5)', lineHeight: '140%' }}>
                  {formatDateTime(inheritance.lastActivity * 1000)}
                </Body2White>
              </Grid>
            </Grid>
          )}
          {ruleInfo.type === RuleType.OnSpecificDate && (
            <Grid item container xs={12} mt="4px">
              <Grid item container xs="auto">
                <Body2White sx={{ color: 'var(--Ebony-Clay-500)', width: '100px' }}>Today:</Body2White>
              </Grid>
              <Grid item container xs alignContent="center">
                <Body2White sx={{ color: 'var(--Ebony-Clay-300, #D2D7E5)', lineHeight: '140%' }}>
                  {formatDateTime(Date.now())}
                </Body2White>
              </Grid>
            </Grid>
          )}

          {inheritance.messages && inheritance.messages !== '' && (
            <Grid item container xs={12} mt="4px">
              <Grid item container xs="auto">
                <Body2White sx={{ color: 'var(--Ebony-Clay-500)', width: '100px' }}>Message:</Body2White>
              </Grid>
              <Grid item container xs alignContent="center">
                <ModalButton
                  titleModal={'Special message for you'}
                  contentModal={<Box mt="16px">{inheritance.messages}</Box>}
                >
                  <MuiLink
                    component="button"
                    sx={{
                      fontSize: '14px',
                      lineHeight: '160%',
                      fontWeight: '500',
                      color: 'var(--Blue-Ribbon-600, #5D5DFF)',
                      textDecorationColor: 'var(--Blue-Ribbon-600, #5D5DFF)',
                      '&:hover': {
                        color: 'var(--Blue-Ribbon-500, #5D5DFF)',
                      },
                    }}
                    underline="always"
                  >
                    View details
                  </MuiLink>
                </ModalButton>
              </Grid>
            </Grid>
          )}
        </Grid>
        {isNoToken ? <NoToken /> : <ListTokens tokenList={tokenList} isModal={isModal} inheritance={inheritance} />}
      </Box>
    </CardContainer>
  )
}

const ListTokens = ({
  tokenList,
  isModal,
  inheritance,
}: {
  tokenList: TokenItem[]
  isModal: boolean
  inheritance: InheritanceOfInheritor
}): ReactElement => {
  const { claimInheritances, sendError, txStatus, isSuccess } = useContext(SendProcessingContext)
  const { setModalOpen } = useContext(ModalButtonContext)
  const { setRefreshInheritor } = useContext(NoticeInheritanceContext)

  const [selected, setSelected] = useState<number[]>([])
  const isDesktop = useDesktop()

  const handleSelect = (index: number) => {
    if (selected.includes(index)) {
      setSelected(selected.filter((i) => i !== index))
    } else {
      setSelected([...selected, index])
    }
  }

  const handleSelectAll = () => {
    if (selected.length === tokenList.length) {
      setSelected([])
    } else {
      setSelected(tokenList.map((_, index) => index))
    }
  }

  useEffect(() => {
    if (isSuccess) {
      setRefreshInheritor((prev) => prev + 1)
      setModalOpen(false)
    }
  }, [isSuccess, setModalOpen, setRefreshInheritor])

  const handleConfirm = () => {
    const tokens = tokenList.filter((_, index) => selected.includes(index)).map((token) => token.address)
    if (tokens.length === 0) return

    claimInheritances(inheritance.legator, tokens)
  }

  const renderFooter = () => {
    return (
      <Grid item xs={12} mt="16px">
        <Grid container>
          {isDesktop && (
            <Grid item xs={5}>
              <Button
                variant="text"
                sx={{
                  paddingY: '10px',
                  width: '100%',
                  borderRadius: '4px',
                  background: 'var(--Ebony-Clay-950, #2F354B)',
                  color: 'var(--Ebony-Clay-300, #D2D7E5)',
                  ':hover': {
                    background: 'rgba(255, 255, 255, 0.08)',
                  },
                }}
                onClick={() => setModalOpen(false)}
              >
                Cancel
              </Button>
            </Grid>
          )}
          {isDesktop && <Grid item xs={2}></Grid>}
          <Grid item xs={isDesktop ? 5 : 12} textAlign="right">
            <Button
              disabled={selected.length === 0}
              variant="contained"
              sx={{
                paddingY: '10px',
                width: '100%',
              }}
              onClick={handleConfirm}
            >
              Claim ({selected.length}/{tokenList.length})
            </Button>
          </Grid>
        </Grid>
      </Grid>
    )
  }
  return (
    <>
      <MuiLink
        component="button"
        sx={{
          marginTop: '16px',
          fontSize: '12px',
          lineHeight: '160%',
          color: 'var(--Emerald-600, #22C55E)',
          textDecorationColor: 'var(--Emerald-600, #22C55E)',
          '&:hover': {
            color: 'var(--Emerald-500, #4ADE80)',
          },
        }}
        underline="always"
        onClick={handleSelectAll}
      >
        {selected.length === tokenList.length
          ? 'Deselect all'
          : tokenList.length > 10
          ? 'Select 10 tokens'
          : 'Select all'}
      </MuiLink>

      <StyledList disablePadding style={{ maxHeight: '435px', overflow: 'auto' }}>
        {tokenList.map((token, index) => (
          <ListItemToken
            key={index}
            logoURI={token.logoURI}
            name={token.name}
            symbol={token.symbol}
            amount={token.amount}
            amountInUSD={token.amountInUSD}
            isModal={isModal}
            index={index}
            isSelected={selected.includes(index)}
            onClick={handleSelect}
          />
        ))}
      </StyledList>
      {sendError !== '' && <Body2White sx={{ color: 'var(--Supporting-Carnation, #FF2239)' }}>{sendError}</Body2White>}
      {renderFooter()}
    </>
  )
}

const ListItemToken = ({
  logoURI,
  name,
  symbol,
  amount,
  amountInUSD,
  isModal,
  index,
  isSelected,
  onClick,
}: {
  logoURI: string
  name: string
  symbol: string
  amount: string
  amountInUSD: string
  isModal: boolean
  index: number
  isSelected: boolean
  onClick: (inder: number) => void
}): ReactElement => {
  const isDesktop = useDesktop()
  return (
    <>
      <ListItem disablePadding>
        <ListItemButton
          sx={isModal && !isDesktop ? { paddingX: '0px' } : {}}
          onClick={() => {
            onClick(index)
          }}
        >
          <Grid container alignItems="center" justifyContent="space-between" sx={{ paddingY: '8px' }}>
            <Grid item container xs="auto" justifyContent="flex-start">
              <Checkbox
                checked={isSelected}
                size="small"
                sx={{
                  padding: '0px',
                  marginRight: isDesktop ? '16px' : '8px',
                  color: 'var(--Ebony-Clay-500)',
                  '&.Mui-checked': {
                    color: 'var(--Emerald-600, #22C55E)',
                  },
                }}
              />
            </Grid>
            <Grid item xs={5} flex={1}>
              <Grid container alignItems="center" justifyContent="flex-start" spacing={2}>
                <Grid item>
                  <TokenIcon logoUri={logoURI} size={32} tokenSymbol={symbol} />
                </Grid>
                <Grid item>
                  <Tooltip title={name} placement="top" arrow>
                    <Typography
                      sx={{
                        color: 'var(--Ebony-Clay-300, #D2D7E5)',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        fontStyle: 'normal',
                        lineHeight: '160%',
                      }}
                      variant="body1"
                      component="h2"
                    >
                      {formatName(name, 10)}
                    </Typography>
                  </Tooltip>
                  <Typography
                    sx={{
                      color: 'var(--Ebony-Clay-500, #7C8AB4)',
                      fontSize: '14px',
                      fontWeight: '400',
                      fontStyle: 'normal',
                      lineHeight: '160%',
                    }}
                    variant="body1"
                    component="h2"
                  >
                    {shortenText(symbol, 6)}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs>
              <Stack>
                <Typography
                  sx={{
                    color: 'var(--Ebony-Clay-300, #D2D7E5)',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    fontStyle: 'normal',
                    lineHeight: '160%',
                    textAlign: 'right',
                  }}
                  variant="body1"
                  component="h2"
                >
                  {amount}{' '}
                  <Tooltip title={symbol} placement="top" arrow>
                    <span
                      style={{
                        color: 'var(--Ebony-Clay-500, #7C8AB4)',
                        fontWeight: '400',
                      }}
                    >
                      {formatName(symbol, 6)}
                    </span>
                  </Tooltip>
                </Typography>
                <Typography
                  sx={{
                    color: 'var(--Ebony-Clay-500, #7C8AB4)',
                    fontSize: '14px',
                    fontWeight: '400',
                    fontStyle: 'normal',
                    lineHeight: '160%',
                    textAlign: 'right',
                  }}
                  variant="body1"
                  component="h2"
                >
                  ~ ${amountInUSD}
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </ListItemButton>
      </ListItem>
      <Divider flexItem />
    </>
  )
}

const NoToken = (): ReactElement => {
  return (
    <>
      <Grid container mt={'48px'} spacing={2} direction="column" alignItems="center" justifyContent="center">
        <Grid item xs={12} alignItems="center" justifyContent="center">
          <Typography
            sx={{
              color: 'var(--Ebony-Clay-600, #5B6B9C)',
              fontSize: '16px',
              fontWeight: '400',
              fontStyle: 'normal',
              lineHeight: '160%',
            }}
            variant="body1"
            component="h2"
          >
            No tokens yet!
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography
            sx={{
              color: 'var(--Ebony-Clay-300, #D2D7E5)',
              fontSize: '16px',
              fontWeight: 'bold',
              fontStyle: 'normal',
              lineHeight: '160%',
            }}
            variant="body1"
            component="h2"
          >
            Let&apos;s Fill Your Wallet: Add Funds Now!
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Btn
            startIcon={
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <g clipPath="url(#clip0_2682_5116)">
                  <path
                    d="M12.6666 8.66668H8.66659V12.6667H7.33325V8.66668H3.33325V7.33334H7.33325V3.33334H8.66659V7.33334H12.6666V8.66668Z"
                    fill="#22C55E"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_2682_5116">
                    <rect width="16" height="16" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            }
          >
            Add funds
          </Btn>
        </Grid>
      </Grid>
    </>
  )
}

export default ClaimableAssets
